import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight, ChevronRight, ChevronLeft } from "lucide-react";
import HealthCirclesLogo from "@/components/HealthCirclesLogo";

const STEPS = ["Credentials", "Professional", "Practice"];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  // Consolidated form
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    mobile: "",
    qualification: "",
    bio: "",
    state_medical_council: "",
    registration_number: "",
    workplace: "",
    preferred_callback_time: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canGoStep1 = form.name.trim() && form.email.trim() && form.password.length >= 8;
  const canGoStep2 = form.qualification.trim() && form.state_medical_council.trim() && form.registration_number.trim();

  const submit = async () => {
    setBusy(true);
    try {
      const data = await register({
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
        mobile: form.mobile.trim() || undefined,
        qualification: form.qualification.trim() || undefined,
        bio: form.bio.trim() || undefined,
        state_medical_council: form.state_medical_council.trim() || undefined,
        registration_number: form.registration_number.trim() || undefined,
        workplace: form.workplace.trim() || undefined,
        preferred_callback_time: form.preferred_callback_time.trim() || undefined,
      });
      if (data?.status === "otp_sent" || data?.status === "pending_approval") {
        toast.success("Check your email — we sent a 6-digit verification code");
        nav(`/verify-otp?email=${encodeURIComponent(form.email.trim())}`);
      } else if (data?.user) {
        toast.success("Welcome to HealthCircles");
        nav("/dashboard");
      } else {
        nav(`/verify-otp?email=${encodeURIComponent(form.email.trim())}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex relative flex-col justify-between p-12 text-white overflow-hidden card-hero-emerald rounded-none">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-white/15 flex items-center justify-center">
            <HealthCirclesLogo size={22} />
          </div>
          <div className="text-lg font-semibold tracking-tight" style={{ fontFamily: "Instrument Sans" }}>HealthCircles</div>
        </div>
        <div className="max-w-md">
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight leading-tight" style={{ fontFamily: "Instrument Sans" }}>
            Start with what matters.
          </h1>
          <p className="mt-5 text-white/80 text-base leading-relaxed">
            Tell us a bit about your practice — we&rsquo;ll get you approved and set up so you can care for patients.
          </p>
          <div className="mt-8 space-y-2 text-white/75 text-sm">
            <div>1. Basic credentials</div>
            <div>2. Professional details</div>
            <div>3. Where you practice</div>
          </div>
        </div>
        <div className="text-white/60 text-sm">© {new Date().getFullYear()} HealthCircles</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-8 md:p-12 bg-[#FDFBF7]">
        <form onSubmit={(e) => e.preventDefault()} className="w-full max-w-md" data-testid="register-form">
          <div className="mb-6">
            <div className="field-label mb-3">Create account</div>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>Join HealthCircles</h2>
          </div>

          {/* Progress */}
          <div className="flex items-center gap-2 mb-6" data-testid="register-progress">
            {STEPS.map((label, i) => (
              <div key={label} className="flex-1">
                <div className={`h-1.5 rounded-full ${i <= step ? "bg-[#0A4C36]" : "bg-[#EFECE5]"}`} />
                <div className={`text-[10px] uppercase tracking-widest mt-1.5 ${i === step ? "text-[#0A4C36] font-semibold" : "text-[#8A9992]"}`}>
                  {label}
                </div>
              </div>
            ))}
          </div>

          {step === 0 && (
            <div className="space-y-4" data-testid="step-credentials">
              <Field label="Full name">
                <input data-testid="register-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className="input-clean" placeholder="Dr. Makesh Gopalakrishnan" />
              </Field>
              <Field label="Email">
                <input data-testid="register-email" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input-clean" placeholder="doctor@clinic.com" />
                <Hint>We&rsquo;ll email you a 6-digit code to verify — no spam ever.</Hint>
              </Field>
              <Field label="Mobile number">
                <input data-testid="register-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} className="input-clean" placeholder="+91 …" />
              </Field>
              <Field label="Password">
                <input data-testid="register-password" required type="password" minLength={8} value={form.password} onChange={(e) => set("password", e.target.value)} className="input-clean" placeholder="At least 8 characters" />
              </Field>
              <StepNav onNext={() => setStep(1)} nextDisabled={!canGoStep1} onBack={null} />
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="step-professional">
              <Field label="Highest qualification">
                <input data-testid="register-qualification" required value={form.qualification} onChange={(e) => set("qualification", e.target.value)} className="input-clean" placeholder="MBBS, MD (General Medicine)" />
              </Field>
              <Field label="State Medical Council">
                <input data-testid="register-council" required value={form.state_medical_council} onChange={(e) => set("state_medical_council", e.target.value)} className="input-clean" placeholder="e.g. Tamil Nadu Medical Council" />
              </Field>
              <Field label="Registration number">
                <input data-testid="register-regno" required value={form.registration_number} onChange={(e) => set("registration_number", e.target.value)} className="input-clean" placeholder="e.g. 123456" />
              </Field>
              <Field label="Short bio">
                <textarea data-testid="register-bio" rows={3} value={form.bio} onChange={(e) => set("bio", e.target.value)} className="input-clean resize-y" placeholder="One or two lines about your practice, focus areas, and years of experience." />
              </Field>
              <StepNav onBack={() => setStep(0)} onNext={() => setStep(2)} nextDisabled={!canGoStep2} />
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="step-practice">
              <Field label="Where do you work or consult?">
                <textarea data-testid="register-workplace" rows={3} value={form.workplace} onChange={(e) => set("workplace", e.target.value)} className="input-clean resize-y" placeholder="Apollo Hospitals, Chennai · Own clinic in Anna Nagar" />
              </Field>
              <Field label="Preferred callback time (optional)">
                <input data-testid="register-callback" value={form.preferred_callback_time} onChange={(e) => set("preferred_callback_time", e.target.value)} className="input-clean" placeholder="e.g. Weekdays 6–8 PM" />
                <Hint>If you&rsquo;d like our team to verify by phone before approval, tell us when to call.</Hint>
              </Field>
              <div className="rounded-2xl bg-[#F7F4EC]/70 border border-[#EFECE5] p-3.5 text-xs text-[#47554D] leading-relaxed">
                By creating an account you confirm the details above are accurate. Admin will review your registration &amp; credentials, and you&rsquo;ll get an activation link once approved.
              </div>
              <div className="flex items-center justify-between gap-2 pt-1">
                <button type="button" onClick={() => setStep(1)} className="btn-outline-emerald" data-testid="register-back">
                  <ChevronLeft size={14} /> Back
                </button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={busy}
                  className="btn-emerald"
                  data-testid="register-submit"
                >
                  {busy ? "Creating…" : (<>Create account <ArrowRight size={16} /></>)}
                </button>
              </div>
            </div>
          )}

          <div className="mt-6 text-sm text-[#6C7B72]">
            Already registered?{" "}
            <Link data-testid="link-login" to="/login" className="text-[#0A4C36] font-semibold hover:underline">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="field-label block mb-2">{label}</label>
      {children}
    </div>
  );
}

function Hint({ children }) {
  return <div className="text-[11px] text-[#8A9992] mt-1.5 leading-snug">{children}</div>;
}

function StepNav({ onBack, onNext, nextDisabled }) {
  return (
    <div className="flex items-center justify-between gap-2 pt-1">
      {onBack ? (
        <button type="button" onClick={onBack} className="btn-outline-emerald" data-testid="register-back">
          <ChevronLeft size={14} /> Back
        </button>
      ) : <span />}
      <button
        type="button"
        onClick={onNext}
        disabled={nextDisabled}
        className="btn-emerald"
        data-testid="register-next"
      >
        Continue <ChevronRight size={14} />
      </button>
    </div>
  );
}
